import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError, mediaUrl } from "../../lib/api";
import { useContent } from "../../context/SiteContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { FieldInput } from "./FieldInput";
import { RESOURCES } from "./resources";

export const defaults = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.type === "images" || f.type === "refs" ? [] : f.type === "switch" ? true : f.type === "select" ? f.options[0] : f.type === "number" ? 100 : ""]));

const Filters = ({ cfg, q, setQ, filters, setFilters, resource }) => (
  <div className="flex flex-wrap gap-3 mb-4">
    {cfg.search && (
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm" data-testid={`admin-search-${resource}`} />
      </div>
    )}
    {cfg.filters?.map((f) => (
      <select key={f.name} value={filters[f.name] ?? ""} onChange={(e) => setFilters({ ...filters, [f.name]: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm" data-testid={`admin-filter-${resource}-${f.name}`}>
        <option value="">All {f.label.toLowerCase()}</option>
        {f.options.map((o, i) => <option key={String(o)} value={String(o)}>{f.labels?.[i] || o}</option>)}
      </select>
    ))}
  </div>
);

export const ResourceManager = ({ resource, title }) => {
  const cfg = RESOURCES[resource];
  const { items, reload } = useContent(resource, true);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});

  const list = useMemo(() => (items || []).filter((it) =>
    (!q || (cfg.search || []).some((k) => String(it[k] || "").toLowerCase().includes(q.toLowerCase()))) &&
    Object.entries(filters).every(([k, v]) => v === "" || String(it[k] ?? (k === "active" ? true : "")) === v)
  ), [items, q, filters, cfg]);
  const filtering = q || Object.values(filters).some((v) => v !== "");

  const move = async (i, dir) => {
    const ids = list.map((x) => x.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try {
      await api.post(`/admin/content/${resource}/reorder`, { ids });
      reload();
    } catch (err) {
      toast.error(fmtError(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) await api.put(`/content/${resource}/${form.id}`, form);
      else await api.post(`/content/${resource}`, form);
      toast.success("Saved — changes are live on the website");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/content/${resource}/${del.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(fmtError(err));
    }
    setDel(null);
  };

  const thumb = (it) => it.image || it.photo || it.logo || it.cover_image || it.thumb || (it.media_type === "photo" && it.url) || it.photos?.[0];

  return (
    <div data-testid={`admin-manager-${resource}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold text-[#0B1F44]">{title || cfg.label}</h2>
        <button onClick={() => setForm(defaults(cfg.fields))} className="btn-primary text-sm" data-testid={`admin-add-${resource}`}><Plus className="h-4 w-4" /> Add new</button>
      </div>
      <Filters cfg={cfg} q={q} setQ={setQ} filters={filters} setFilters={setFilters} resource={resource} />
      {cfg.note && <p className="text-xs text-slate-500 mb-3" data-testid={`admin-note-${resource}`}>{cfg.note}</p>}
      {cfg.reorder && !filtering && list.length > 1 && <p className="text-xs text-slate-500 mb-2">Use the arrows to change the display order on the website.</p>}
      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
        {!items && <p className="p-8 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>}
        {items && list.length === 0 && <p className="p-8 text-center text-slate-500" data-testid={`admin-empty-${resource}`}>{filtering ? "No matching items." : "No items yet."}</p>}
        {list.map((it, i) => (
          <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-row-${resource}-${it.id}`}>
            {cfg.reorder && !filtering && (
              <div className="flex flex-col">
                <button disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-20" data-testid={`admin-up-${resource}-${it.id}`} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-20" data-testid={`admin-down-${resource}-${it.id}`} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
            )}
            <div className="h-12 w-12 rounded-lg bg-slate-100 overflow-hidden shrink-0">{thumb(it) && <img src={mediaUrl(thumb(it))} alt="" className="h-full w-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#0B1F44] truncate">{it[cfg.titleKey] || "(untitled)"}</p>
              <p className="text-xs text-slate-500 truncate">{it[cfg.subKey]}</p>
            </div>
            {cfg.badges?.(it).map((b) => <span key={b} className="rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2 py-0.5 text-xs font-semibold" data-testid={`admin-badge-${resource}-${it.id}`}>{b}</span>)}
            <button onClick={() => setForm({ ...defaults(cfg.fields), ...it })} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`admin-edit-${resource}-${it.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => setDel(it)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`admin-delete-${resource}-${it.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-form-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "Add"} – {cfg.label}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.name} className="block">
                  <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}{f.required && " *"}</span>
                  <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} form={form} setForm={setForm} />
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setForm(null)} className="btn-outline text-sm" data-testid="admin-form-cancel">Cancel</button>
                <button disabled={busy || cfg.fields.some((f) => f.required && !form[f.name])} className="btn-primary text-sm disabled:opacity-50" data-testid="admin-form-save">{busy ? "Saving..." : "Save"}</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>"{del?.[cfg.titleKey]}" will be removed from the website.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
